import { useEffect, useRef } from "react";

/**
 * Animated circular loop diagram showing the Ecospora circular economy:
 * LKTN → Beglog → Asnaf Growers → Buyback → Market → Reinvest → back.
 * Pure SVG + CSS rotate animation. No external animation library.
 */
const NODES = [
  { label: "LKTN", sub: "Funds the beglog" },
  { label: "Beglog", sub: "Kenaf substrate" },
  { label: "Asnaf", sub: "Families grow" },
  { label: "Buyback", sub: "Guaranteed offtake" },
  { label: "Market", sub: "Brands & partners" },
  { label: "Reinvest", sub: "Loop closes" },
];

const CircularLoop = ({ className = "" }) => {
  const groupRef = useRef(null);
  const cx = 250;
  const cy = 250;
  const r = 170;

  useEffect(() => {
    // Use ref for cleanliness; animation handled via CSS class
  }, []);

  return (
    <div className={`relative w-full max-w-[520px] aspect-square mx-auto ${className}`} data-testid="circular-loop">
      <svg viewBox="0 0 500 500" className="w-full h-full">
        <defs>
          <marker id="arrow" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
            <path d="M0,0 L10,5 L0,10 z" fill="#2C4A3E" />
          </marker>
        </defs>

        {/* Outer rotating dashed ring */}
        <g ref={groupRef} className="loop-rotate" style={{ transformBox: "fill-box", transformOrigin: "center" }}>
          <circle
            cx={cx}
            cy={cy}
            r={r + 25}
            fill="none"
            stroke="#C9A961"
            strokeWidth="1"
            strokeDasharray="2 8"
            opacity="0.5"
          />
        </g>

        {/* Static inner ring */}
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#2C4A3E" strokeWidth="1.25" opacity="0.4" />

        {/* Centre */}
        <circle cx={cx} cy={cy} r="60" fill="#F5F1E8" stroke="#2C4A3E" strokeWidth="1" />
        <text
          x={cx}
          y={cy - 6}
          textAnchor="middle"
          fontFamily="Fraunces, serif"
          fontSize="20"
          fontWeight="600"
          fill="#2C4A3E"
        >
          Ecospora
        </text>
        <text
          x={cx}
          y={cy + 18}
          textAnchor="middle"
          fontFamily="Inter, sans-serif"
          fontSize="9"
          letterSpacing="2"
          fill="#6B6B65"
        >
          THE PLATFORM
        </text>

        {/* Nodes */}
        {NODES.map((node, i) => {
          const angle = (i / NODES.length) * Math.PI * 2 - Math.PI / 2;
          const x = cx + Math.cos(angle) * r;
          const y = cy + Math.sin(angle) * r;
          const labelOffset = 18;
          const lx = cx + Math.cos(angle) * (r + labelOffset);
          const ly = cy + Math.sin(angle) * (r + labelOffset);
          const align =
            Math.abs(Math.cos(angle)) < 0.2
              ? "middle"
              : Math.cos(angle) > 0
              ? "start"
              : "end";
          return (
            <g key={node.label}>
              {/* Connecting curved arc to next node */}
              {(() => {
                const nextAngle = ((i + 1) / NODES.length) * Math.PI * 2 - Math.PI / 2;
                const nx = cx + Math.cos(nextAngle) * r;
                const ny = cy + Math.sin(nextAngle) * r;
                return (
                  <path
                    d={`M ${x} ${y} A ${r} ${r} 0 0 1 ${nx} ${ny}`}
                    fill="none"
                    stroke="#2C4A3E"
                    strokeWidth="1.5"
                    opacity="0.85"
                  />
                );
              })()}
              <circle cx={x} cy={y} r="9" fill="#F5F1E8" stroke="#2C4A3E" strokeWidth="1.5" />
              <circle cx={x} cy={y} r="3" fill="#C9A961" />
              <text
                x={lx}
                y={ly - 2}
                textAnchor={align}
                fontFamily="Fraunces, serif"
                fontSize="14"
                fontWeight="600"
                fill="#2C4A3E"
              >
                {String(i + 1).padStart(2, "0")} · {node.label}
              </text>
              <text
                x={lx}
                y={ly + 13}
                textAnchor={align}
                fontFamily="Inter, sans-serif"
                fontSize="10"
                fill="#6B6B65"
              >
                {node.sub}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

export default CircularLoop;
